import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, TextField,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Chip, IconButton, Tooltip, Skeleton, useTheme,
  LinearProgress,
} from '@mui/material';
import { Undo, FilterList, ReceiptLong } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import finePaymentService from '../../services/finePaymentService';
import { useDebounce } from '../../hooks/useDebounce';
import { FinePayment, FinePaymentMethod } from '../../types/letters';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import ReasonDialog from '../common/ReasonDialog';
import { useReceipts } from '../finance/useReceipts';
import BusyIcon from '../common/BusyIcon';
import { PAYMENT_METHOD_LABEL, PAYMENT_METHOD_OPTIONS } from '../../utils/paymentMethods';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useTimezone } from '../../hooks/useTimezone';
import { severityChipSx } from '../../utils/severityTone';
import DateRangePicker from '../common/DateRangePicker';
import SearchableSelect from '../common/SearchableSelect';


const CollectedFinesTable: React.FC<{ refreshKey?: number; onChanged?: () => void }> = ({ refreshKey, onChanged }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const canReverse = canWrite('fines');
  const fmt = useCurrencyFormat();
  const { formatDate } = useTimezone();
  const { downloadFineReceipt, preparingId } = useReceipts();

  const [items, setItems] = useState<FinePayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  // 300ms debounce — typing doesn't fire a re-fetch / filter pass per keystroke.
  const debouncedSearch = useDebounce(search, 300);
  const [method, setMethod] = useState<FinePaymentMethod | 'all'>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [reverseTarget, setReverseTarget] = useState<FinePayment | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await finePaymentService.listFinePayments({
        method,
        from: from || undefined,
        to: to || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      let rows = res.items;
      const q = debouncedSearch.trim().toLowerCase();
      if (q) {
        rows = rows.filter(r => {
          const studentName = typeof r.studentId === 'string' ? '' : (r.studentId?.name || '');
          const ref = (r.referenceNumber || '').toLowerCase();
          return studentName.toLowerCase().includes(q) || ref.includes(q);
        });
      }
      setItems(rows);
      setTotalCount(res.pagination.totalItems);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to load payments', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [method, from, to, page, rowsPerPage, debouncedSearch, enqueueSnackbar]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Reset to page 0 when the debounced search resolves to a new value.
  useEffect(() => { setPage(0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [debouncedSearch]);

  const pageTotal = useMemo(() => items.reduce((s, r) => s + (r.amount || 0), 0), [items]);

  const handleReverse = async (reason: string) => {
    if (!reverseTarget) return;
    await finePaymentService.reverseFinePayment(reverseTarget._id, reason);
    enqueueSnackbar('Fine payment reversed', { variant: 'success' });
    load();
    onChanged?.();
  };

  return (
    <Box>
      {/* Filters */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <FilterList sx={{ color: 'text.disabled', display: { xs: 'none', md: 'block' } }} />
          <TextField
            size="small" placeholder="Search student or reference"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
          />
          <SearchableSelect
            label="Method"
            value={method}
            onChange={(v) => { setMethod((v || 'all') as any); setPage(0); }}
            options={[
              { value: 'all', label: 'All methods' },
              ...PAYMENT_METHOD_OPTIONS,
            ]}
            clearable={false}
            minWidth={160}
          />
          <DateRangePicker
            label="Date range"
            value={{ from: from || null, to: to || null }}
            onChange={(r) => { setFrom(r.from || ''); setTo(r.to || ''); setPage(0); }}
            minWidth={260}
          />
          <Box sx={{ flex: 1 }} />
          <Stack alignItems={{ md: 'flex-end' }}>
            <Typography variant="caption" color="text.secondary">Page total</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.success.main }}>{fmt(pageTotal)}</Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', position: 'relative' }}>
        {loading && items.length > 0 && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1 }}
          />
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Collected by</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Skeleton rows only when we have no rows at all (first load).
                  Subsequent refetches keep the body stable — only the slim
                  progress strip above the table moves. */}
              {loading && items.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body1" color="text.secondary">No payments in this range</Typography>
                    <Typography variant="caption" color="text.disabled">Adjust the filters above.</Typography>
                  </TableCell>
                </TableRow>
              ) : items.map(p => {
                const studentName = typeof p.studentId === 'string' ? '' : (p.studentId?.name || '');
                const letter = typeof p.letterId === 'string' ? null : p.letterId;
                const category = letter?.categoryId;
                return (
                  <TableRow key={p._id} hover>
                    <TableCell>
                      <Typography variant="body2">{formatDate(p.collectedAt)}</Typography>
                      {p.receiptNumber && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{p.receiptNumber}</Typography>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight={500}>{studentName || '—'}</Typography></TableCell>
                    <TableCell>
                      {category && (
                        <Chip label={category.label} size="small" sx={severityChipSx(theme, (category as any).severity)} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.success.main }}>{fmt(p.amount)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={PAYMENT_METHOD_LABEL[p.method]} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{p.referenceNumber || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{(p.collectedBy as any)?.name || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                        {p.notes || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={preparingId === p._id ? 'Preparing…' : 'Download receipt'}>
                        <IconButton size="small" onClick={() => { if (!preparingId) downloadFineReceipt(p); }}>
                          <BusyIcon busy={preparingId === p._id}><ReceiptLong fontSize="small" /></BusyIcon>
                        </IconButton>
                      </Tooltip>
                      {canReverse && (
                        <Tooltip title="Reverse this payment">
                          <IconButton size="small" onClick={() => setReverseTarget(p)}>
                            <Undo fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <ReasonDialog
        open={Boolean(reverseTarget)}
        onClose={() => setReverseTarget(null)}
        title="Reverse this fine payment?"
        facts={reverseTarget ? [
          { label: 'Amount', value: fmt(reverseTarget.amount) },
          { label: 'Student', value: typeof reverseTarget.studentId === 'object' ? reverseTarget.studentId.name : '—' },
          { label: 'Collected', value: formatDate(reverseTarget.collectedAt) },
        ] : []}
        consequence="The fine becomes due again for this amount and the reversal is recorded in the books today. The payment stays on record, marked reversed."
        confirmText="Reverse payment"
        onConfirm={handleReverse}
      />
    </Box>
  );
};

export default CollectedFinesTable;
